import { useMutation, useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import {
  AlterarUnidadeMedidaDtoType,
  unidadeDeMedidaBreadcrumbItems,
} from "data/unidadesDeMedida";
import { transformErrors } from "helpers/utils";
import { useEffect } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Navigate, useParams } from "react-router-dom";
import { usePermissionModal } from "contexts/PermissionModalContext";

const EditarUnidadeDeMedida = () => {
  const { id } = useParams();
  const { getQueryRetryHandler, getMutationErrorHandler } = usePermissionModal();
  const errorMessage = "Você não possui permissão para editar esta unidade de medida";

  const { isLoading, isError, data } = useQuery<{
    data: AlterarUnidadeMedidaDtoType;
  }>({
    queryKey: ["unidadeMedida", id],
    queryFn: () => axios.get(`/api/unidadeMedida/${id}`),
    enabled: !!id,
    retry: getQueryRetryHandler({
      queryKey: ["unidadeMedida", id],
      defaultMessage: "Você não possui permissão para visualizar esta unidade de medida"
    })
  });

  const handleFormErrors = (error: any) => {
    // Tratando o erro de forma segura com tipagem
    const errorData = error.response?.data as { errors?: Record<string, any> } || {};
    const formattedErrors = transformErrors(errorData.errors || {});
    for (const [field, errorValue] of Object.entries(formattedErrors)) {
      form.setError(field as any, errorValue as any);
    }
  };

  const mutation = useMutation<
    any,
    AxiosError<any>,
    { id: string; data: AlterarUnidadeMedidaDtoType }
  >({
    mutationFn: ({ id, data }) => axios.put(`/api/unidadeMedida/${id}`, data),
    onError: getMutationErrorHandler(errorMessage, handleFormErrors)
  });

  const unidadeMedida = data?.data;

  const form = useForm<AlterarUnidadeMedidaDtoType>();

  useEffect(() => {
    if (unidadeMedida) {
      console.log(unidadeMedida);
      form.reset(unidadeMedida);
    }
  }, [unidadeMedida]);

  if (isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );

  if (isError) {
    return <div>Ocorreu um erro ao carregar a unidade de medida</div>;
  }

  if (!unidadeMedida) {
    return <div>Unidade de medida não encontrada</div>;
  }

  const onSubmit: SubmitHandler<AlterarUnidadeMedidaDtoType> = (data) => {
    mutation.mutate({ data, id: id as string });
  };

  if (mutation.isSuccess) {
    return <Navigate to={`/cadastros/unidades-de-medida`} />;
  }

  return (
    <div>
      <FormProvider {...form}>
        <PageBreadcrumb
          items={unidadeDeMedidaBreadcrumbItems(unidadeMedida?.sigla)}
        />
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Editar uma unidade de medida</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Atualize as informações da unidade de medida
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="phoenix-secondary"
                type="button"
                onClick={() => form.reset()}
              >
                Descartar
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={mutation.isPending}
                disabled={mutation.isPending}
                loadingPosition="start"
              >
                Salvar
              </Button>
            </div>
          </div>
          <Row className="g-5">
            <Col xs={12} xl={8}>
              <h4 className="mb-3">Informações da unidade</h4>
              <Row className="g-2">
                <Form.Group
                  as={Col}
                  xs={2}
                  xl={2}
                  className="mb-5"
                  controlId="sigla"
                >
                  <Form.Control
                    placeholder="Sigla da unidade"
                    {...form.register("sigla")}
                    required
                    isInvalid={!!form.formState.errors.sigla}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.sigla?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  xs={8}
                  xl={10}
                  className="mb-5"
                  controlId="descricao"
                >
                  <Form.Control
                    placeholder="Descrição da unidade"
                    {...form.register("descricao")}
                    required
                    isInvalid={!!form.formState.errors.descricao}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.descricao?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </div>
  );
};

export default EditarUnidadeDeMedida;
